import "./Footer.css";
import { Linkedin, Instagram, Facebook, MapPin } from "lucide-react";
import { content } from "@/lib/content";
import { useLanguage } from "@/lib/language";
import { SOCIAL_PROFILES } from "@/lib/site";
import { LangLink } from "@/components/LangLink";

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function Footer() {
  const { t } = useLanguage();
  const f = content.footer;

  const links = [
    { label: content.nav.home, to: "/" as const },
    { label: content.nav.about, to: "/about" as const },
    { label: content.nav.services, to: "/services" as const },
    { label: content.nav.industries, to: "/industries" as const },
    { label: content.nav.contact, to: "/contact" as const },
    {
      label: { en: "Insights", ar: "مقالات وأدلة" },
      to: "/insights",
    },
    { label: { en: "ISO 9001 certification", ar: "شهادة الأيزو 9001" }, to: "/services/iso-9001" },
    {
      label: { en: "Vendor registration", ar: "تسجيل موردي النفط والغاز" },
      to: "/services/oil-gas-registration",
    },
    {
      label: { en: "ISO cost & process guide", ar: "دليل تكلفة الأيزو وإجراءاته" },
      to: "/insights/iso-certification-cost-and-process-in-iraq",
    },
    { label: content.nav.privacy, to: "/privacy" as const },
  ];

  const socialIcons = {
    X: XIcon,
    LinkedIn: Linkedin,
    Instagram,
    Facebook,
    "Google Business Profile": MapPin,
  };
  const socials = SOCIAL_PROFILES.map((p) => ({ ...p, Icon: socialIcons[p.label] }));

  return (
    <footer
      data-testid="site-footer"
      className="flex flex-col bg-[oklch(0.13_0.01_25)] text-topbar-foreground"
    >
      {/* CLEAN CONTENT GRID */}
      <div
        className={`footer-grid mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-4 py-16 lg:px-8 ${
          socials.length ? "md:grid-cols-4" : "md:grid-cols-3"
        }`}
      >
        {/* COL 1 — BRAND + TAGLINE */}
        <div className="footer-brand text-start">
          <span className="text-2xl font-extrabold tracking-tight text-background">
            Iraq<span className="text-primary">Cert</span>
          </span>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#d1d5db]">{t(f.tagline)}</p>
          <div className="mt-5 max-w-xs rounded-lg border border-white/10 bg-white/5 p-3.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-background/90">
              {t(f.accreditation.label)}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-[#d1d5db]">
              {t(f.accreditation.body)}
            </p>
            <a
              href={f.accreditation.verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-semibold text-[#ff9494] transition-colors hover:text-white hover:underline"
            >
              {t(f.accreditation.verify)} →
            </a>
          </div>
        </div>

        {/* COL 2 — QUICK LINKS */}
        <div className="footer-links text-start">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-background">
            {t(f.quickLinks)}
          </h3>
          <ul className="mt-5 space-y-2.5">
            {links.map((link) => (
              <li key={link.label.en}>
                <LangLink
                  to={link.to}
                  className="text-sm text-[#d1d5db] transition-colors hover:text-white"
                >
                  {t(link.label)}
                </LangLink>
              </li>
            ))}
          </ul>
        </div>

        {/* COL 3 — CONTACT (FLUSH TEXT LIST) */}
        <div className="footer-contact text-start">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-background">
            {t(f.contactTitle)}
          </h3>
          <ul className="mt-5 space-y-4">
            {f.offices.map((o) => (
              <li key={o.id} className="grid justify-items-start">
                <p className="text-sm font-bold text-background">{t(o.city)}</p>
                <a
                  href={`tel:+${o.wa.split("/").pop()}`}
                  dir="ltr"
                  className="text-sm text-[#d1d5db] transition-colors hover:text-white rtl:text-end rtl:block"
                >
                  {o.phone}
                </a>
                <a
                  href={o.wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-[#d1d5db] transition-colors hover:text-white"
                >
                  {t({ en: "Open WhatsApp", ar: "افتح واتساب" })}
                </a>
              </li>
            ))}
            <li>
              <a
                href={`mailto:${f.email.en}`}
                className="text-sm text-[#d1d5db] transition-colors hover:text-white"
              >
                {f.email.en}
              </a>
            </li>
          </ul>
        </div>

        {/* COL 4 — SOCIAL (hidden until real profiles exist in SOCIAL_PROFILES) */}
        {socials.length > 0 && (
          <div className="footer-social text-start">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-background">
              {t(f.socialTitle)}
            </h3>
            <div className="mt-5 flex items-center gap-3">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-[#d1d5db] transition-colors hover:text-white"
                >
                  <Icon className="size-5" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* COPYRIGHT + CREDIT */}
      <div className="footer-credits mx-auto flex w-full max-w-7xl flex-col gap-1 px-4 pb-6 text-start text-xs text-[#d1d5db] sm:flex-row sm:items-center sm:justify-between lg:px-8">
        <span>
          © {new Date().getFullYear()} IraqCert. {t(f.rights)}
        </span>
        <span>
          {t({ en: "Website by", ar: "تصميم وتطوير" })}{" "}
          <a
            href="https://keybridge.krd"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-white"
          >
            Keybridge
          </a>
        </span>
      </div>

      {/* MASSIVE FULL-BLEED SVG BASE — ANCHORED TO BOTTOM EDGE */}
      <div className="mt-auto w-full max-w-none overflow-hidden px-0 pb-0">
        <svg
          viewBox="0 0 1000 150"
          className="block h-auto w-full select-none"
          preserveAspectRatio="xMidYMax meet"
          aria-hidden
        >
          <text
            x="500"
            y="135"
            textAnchor="middle"
            fontFamily="system-ui, Arial, sans-serif"
            fontWeight="900"
            fontSize="160"
            letterSpacing="-6"
          >
            <tspan fill="#1A1A1A">IRAQ</tspan>
            <tspan fill="#4A0404">CERT</tspan>
          </text>
        </svg>
      </div>
    </footer>
  );
}
